/**
 * The viewport for Lit components: `uiState.viewport`, the visible area and
 * the axis it implies, refreshed on resize, orientation change and visual
 * viewport changes (the mobile URL bar collapsing, the keyboard). Most sizing
 * belongs in a component's own CSS (media and container queries); this is for
 * what CSS cannot express, such as choosing between two whole layouts.
 *
 * Deliberately separate from `device.mobile` (game-side, Pixi's isMobile, for
 * platform, renderer and audio decisions): space versus platform identity.
 * Components pick whichever is right for their concern.
 *
 * Until 2026-10-21 this also kept `uiState.input`, the pointer and hover
 * capabilities from four media queries, meant to decide whether the touch
 * controls should exist; nothing ever read it (the touch controls mount on
 * isMobile), so it went with its queries and listeners.
 */

import { uiState } from './UIState';

/** Coalesces multiple rapid events (e.g. resize during drag, scroll on mobile
 *  visual-viewport shift) into one update per frame. */
let pendingFrame: number | null = null;

/** The visually-visible area. Exported for components that need to measure
 *  imperatively (a drag handler, a position clamp) rather than react to
 *  `uiState.viewport`, which this module only refreshes on the next frame. */
export function getViewportSize(): { width: number; height: number } {
  // visualViewport reflects the actually-visible area, accounting for the
  // mobile URL bar collapsing, virtual keyboard, and pinch zoom. Falls back
  // to innerWidth/innerHeight on browsers without it (vanishingly rare in 2026).
  return {
    width: Math.round(window.visualViewport?.width ?? window.innerWidth),
    height: Math.round(window.visualViewport?.height ?? window.innerHeight),
  };
}

function recompute(): void {
  pendingFrame = null;

  const { width, height } = getViewportSize();

  uiState.viewport.width = width;
  uiState.viewport.height = height;
  uiState.viewport.shortSide = Math.min(width, height);
  uiState.viewport.longSide = Math.max(width, height);
  uiState.viewport.orientation = width >= height ? 'landscape' : 'portrait';
}

function schedule(): void {
  if (pendingFrame !== null) return;
  pendingFrame = requestAnimationFrame(recompute);
}

/**
 * Measures the viewport once and keeps `uiState.viewport` in step with the
 * browser from then on. Call once at boot, before any Lit component renders.
 *
 * Returns a teardown function — important under Vite HMR where this module
 * may re-execute, and useful for tests. In normal production boot the return
 * value can be ignored.
 */
export function initResponsive(): () => void {
  recompute();

  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  window.visualViewport?.addEventListener('resize', schedule);
  window.visualViewport?.addEventListener('scroll', schedule);

  return () => {
    window.removeEventListener('resize', schedule);
    window.removeEventListener('orientationchange', schedule);
    window.visualViewport?.removeEventListener('resize', schedule);
    window.visualViewport?.removeEventListener('scroll', schedule);

    if (pendingFrame !== null) {
      cancelAnimationFrame(pendingFrame);
      pendingFrame = null;
    }
  };
}
